import Store from '@/store';
import { FirebaseService } from './firebase';
import { LocationSyncer } from './locationSyncer';
import * as turf from '@turf/turf';
import CapacitorService, { GeolocationPosition } from './capacitor';
import BackgroundGeolocationService, { BackgroundGeolocationWatcher } from './backgroundGeolocation';
import { Notification } from './notification';
import { LoggingService } from './logging';

const DISTANCE_THRESHOLD_METERS = 30.48; // 100 feet in meters
const TIME_THRESHOLD_MS = 1000 * 60 * 10; // 10 minutes in milliseconds

let watcher: BackgroundGeolocationWatcher | null = null;
let expirationCheckInterval: ReturnType<typeof setInterval> | null = null;
let watcherTimeout: ReturnType<typeof setTimeout> | null = null;

export const LocationTrackingService = {
  async startTracking(duration?: number, endTime?: number): Promise<{ success: boolean; error?: string }> {
    LoggingService.Log('Location tracking start requested');

    if (watcher !== null) {
      // Already tracking
      LoggingService.Log('Location tracking already active, skipping start');
      return { success: true };
    }

    try {
      // Request permissions first
      LoggingService.Log('Requesting location permissions');
      const hasPermission = await BackgroundGeolocationService.requestPermissions();

      if (!hasPermission) {
        LoggingService.Log('Location permission denied by user');
        console.error('Location permission denied');
        return { success: false, error: 'Location permission denied' };
      }

      LoggingService.Log('Location permissions granted');

      // Set up duration tracking if provided
      if (duration && endTime) {
        // Only set startTime if it's not already set (new session vs resume)
        const existingStartTime = Store.state.trackingStartTime;
        const startTime = existingStartTime || Date.now();

        LoggingService.Log(`Setting tracking duration: ${duration}ms, end time: ${new Date(endTime).toISOString()}`);
        Store.commit('setTrackingDuration', duration);
        Store.commit('setTrackingTimes', { startTime, endTime });

        // Start checking for expiration every 30 seconds
        this.startExpirationCheck();
      }

      // Use background geolocation (automatically falls back to foreground on web)
      LoggingService.Log('Starting background geolocation watch');

      // Set a timeout in case the watch callback is never called
      // This can happen in some browsers (e.g., Firefox) when permissions are unclear
      watcherTimeout = setTimeout(async () => {
        if (watcher !== null) {
          LoggingService.Log('Location watch timed out - no successful response from geolocation API');
          const errorMessage = 'Unable to get location - request timed out';
          Store.commit('setLocationTrackingError', errorMessage);
          Notification.methods.send(false, 'Location Error', errorMessage);
          await LocationTrackingService.stopTracking();
        }
      }, 100000); // 100 seconds timeout

      // Commit this first because of race conditions where we immediately fail the callback.
      // If not then we would set location tracking true again.
      Store.commit('setLocationTracking', true);
      watcher = await BackgroundGeolocationService.watchPosition(
        async (position, error) => {
          LoggingService.Log('Location watch callback received');

          if (error) {
            const errorMessage = error?.message || error?.toString() || 'Unknown error';
            LoggingService.Log(`Location watch error: ${errorMessage}`);
            console.error('Error watching position:', error);

            const code = error?.code ?? 0;

            // Check if it's a permission error
            const isPermissionError = errorMessage.toLowerCase().includes('permission') ||
              errorMessage.toLowerCase().includes('denied')
              || code === 1;

            if (isPermissionError) {
              LoggingService.Log('Permission error detected, stopping tracking');
              Store.commit('setLocationTrackingError', errorMessage);
              Notification.methods.send(false, 'Location Error', errorMessage);
              await LocationTrackingService.stopTracking(false);
              // Clear the timeout since we got a callback
              if (watcherTimeout) {
                clearTimeout(watcherTimeout);
                watcherTimeout = null;
              }
              return;
            } else {
              // Timeout - just log, don't notify user
              LoggingService.Log('Location request timed out, will retry');
            }
            return;
          }

          if (position) {
            // Clear the timeout since we got a callback
            if (watcherTimeout) {
              clearTimeout(watcherTimeout);
              watcherTimeout = null;
            }
            LoggingService.Log(`Location received: lat=${position.coords.latitude}, lon=${position.coords.longitude}, accuracy=${position.coords.accuracy}m`);

            try {
              await LocationTrackingService.handleLocationUpdate(position);
            } catch (err) {
              LoggingService.Log(`Error handling location update: ${err}`);
              console.error('Error handling location update:', err);
            }
          }
        },
        {
          backgroundMessage: 'PIT Count is sharing your route to help coordinate coverage.',
          backgroundTitle: 'Route Sharing Active',
          requestPermissions: true,
          stale: false,
          distanceFilter: 30, // 30 meters (~100 feet)
        }
      );
      LoggingService.Log('Location tracking started successfully');
      return { success: true };
    } catch (error: any) {
      // Clear the timeout since we got an error
      if (watcherTimeout) {
        clearTimeout(watcherTimeout);
        watcherTimeout = null;
      }

      LoggingService.Log(`Failed to start location tracking: ${error?.message || error}`);
      console.error('Error starting location tracking:', error);
      return { success: false, error: error?.message || 'Unable to start location tracking' };
    }
  },

  async stopTracking(showNotification = false) {
    LoggingService.Log('Stopping location tracking');

    if (watcher !== null) {
      await watcher.remove();
      watcher = null;
      LoggingService.Log('Location watch removed');
    }

    if (watcherTimeout) {
      clearTimeout(watcherTimeout);
      watcherTimeout = null;
    }

    this.stopExpirationCheck();
    Store.commit('setLocationTracking', false);
    Store.commit('clearTrackingTimes');
    // Note: We don't clear capturedLocations here so stats can still be displayed
    // after tracking stops. They will be cleared when starting a new session.

    LoggingService.Log('Location tracking stopped');

    if (showNotification) {
      // Notification will be handled by the component
      return true;
    }
    return false;
  },

  async handleLocationUpdate(position: GeolocationPosition) {
    const newCoords: [number, number] = [
      position.coords.longitude,
      position.coords.latitude,
    ];
    const newTimestamp = Date.now();

    LoggingService.Log(`Processing location update: [${newCoords[0]}, ${newCoords[1]}]`);

    // Update the general location in store on EVERY position update
    // This allows Map and Survey components to use the latest location
    // without making additional geolocation API calls
    Store.commit('updateLocation', newCoords);

    const lastTracked = Store.state.lastTrackedLocation;

    // Check if we should save this location
    let shouldSave = false;
    if (!lastTracked) {
      // First location, always save
      LoggingService.Log('First location detected, will save to Firebase');
      shouldSave = true;
    } else {
      const timeDiff = newTimestamp - lastTracked.timestamp;
      const hasEnoughTimePassed = timeDiff >= TIME_THRESHOLD_MS;

      // Calculate distance using Turf.js
      const from = turf.point(lastTracked.coords);
      const to = turf.point(newCoords);
      const distanceMeters = turf.distance(from, to, { units: 'meters' });
      const isFarEnough = distanceMeters >= DISTANCE_THRESHOLD_METERS;

      LoggingService.Log(`Location thresholds - Time: ${(timeDiff / 1000).toFixed(0)}s (need ${TIME_THRESHOLD_MS / 1000}s), Distance: ${distanceMeters.toFixed(1)}m (need ${DISTANCE_THRESHOLD_METERS}m)`);

      shouldSave = hasEnoughTimePassed || isFarEnough;

      if (shouldSave) {
        LoggingService.Log(`Location will be saved - ${hasEnoughTimePassed ? 'time threshold met' : 'distance threshold met'}`);
      } else {
        LoggingService.Log('Location skipped - thresholds not met');
      }
    }

    if (shouldSave) {
      const location = {
        coords: newCoords,
        timestamp: newTimestamp,
      };

      // Update last tracked location in store
      Store.commit('updateLastTrackedLocation', location);

      // Add to unsynced locations immediately
      Store.commit('addUnsyncedLocation', location);

      // Attempt to sync all unsynced locations
      await LocationSyncer.trySubmitUnsynced();
    }
  },

  isTracking(): boolean {
    return watcher !== null;
  },

  startExpirationCheck() {
    this.stopExpirationCheck();

    expirationCheckInterval = setInterval(() => {
      this.checkAndStopIfExpired();
    }, 30000); // Check every 30 seconds
  },

  stopExpirationCheck() {
    if (expirationCheckInterval) {
      clearInterval(expirationCheckInterval);
      expirationCheckInterval = null;
    }
  },

  async checkAndStopIfExpired(): Promise<boolean> {
    const endTime = Store.state.trackingEndTime;

    if (!endTime) {
      return false;
    }

    const now = Date.now();
    if (now >= endTime) {
      // Time has expired, stop tracking
      LoggingService.Log('Tracking duration expired, stopping automatically');
      await this.stopTracking(true);
      return true;
    }

    return false;
  },

  getRemainingTime(): number | null {
    const endTime = Store.state.trackingEndTime;
    if (!endTime) return null;

    const remaining = endTime - Date.now();
    return remaining > 0 ? remaining : 0;
  },
};
